import { defineStore } from 'pinia'

interface GeoState {
	country: string | null
	city: string | null
	continent: string | null
	region: string | null
	regionCode: string | null
	postalCode: string | null
	isEU: boolean
}

/**
 * Snapshot of `window.__GEO__` injected by the Cloudflare Worker (see `worker.ts`).
 * `undefined` during `npm run serve` — there is no dev-mode fallback, so consumers
 * must tolerate empty fields. Read once at store creation; geo cannot change within
 * a session.
 */
export const useGeoStore = defineStore('geo', {
	state: (): GeoState => {
		const g = (typeof window !== 'undefined' && window.__GEO__) || {}
		return {
			country: g.country ?? null,
			city: g.city ?? null,
			continent: g.continent ?? null,
			region: g.region ?? null,
			regionCode: g.regionCode ?? null,
			postalCode: g.postalCode ?? null,
			isEU: g.isEUCountry === '1',
		}
	},

	getters: {
		/**
		 * California's "Click to Cancel" law (CA AB-2863, effective 2025) requires that
		 * cancelling a recurring subscription must be as frictionless as signing up —
		 * the user must be able to cancel in essentially one click. We mirror this in
		 * the UI by removing the "answer the survey first" gate on every cancellation
		 * screen for visitors whose Cloudflare-resolved region is California.
		 *
		 * Cloudflare's `cf.regionCode` for the US is the ISO 3166-2 sub-region code
		 * without the `US-` prefix, so California arrives as `'CA'` paired with
		 * `country === 'US'`. We require both to avoid colliding with Canada
		 * (`country === 'CA'`, where `regionCode` is e.g. `'ON'`).
		 */
		isOneClickCancelRequired(state): boolean {
			return state.country === 'US' && state.regionCode === 'CA'
		},
	},
})
